"use client"

import { useEffect, useState } from "react"
import { Plus, Globe, Edit, Trash2 } from "lucide-react"

import { Button } from "../ui/button"
import EmptyState from "../ui/EmptyState"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog"

import { useToast } from "../../hooks/use-toast"
import { useOrganization } from "../../contexts/OrganizationContext"
import credentialService from "../../services/credential.service"
import { PRIMARY, BUTTON_SIZE } from "../../lib/buttonStyles"
import { ApiConnectionDialog } from "./api-connection-dialog"
import { AUTH_TYPES, buildApiCredentialPayload } from "./apiCredentialForm"

const toListRow = (c) => ({
  id: c.id,
  name: c.name,
  base_url: c.base_url,
  auth_type: c.auth_params?.auth_type,
})

const authLabel = (type) => AUTH_TYPES.find((t) => t.value === type)?.label || "None"

/**
 * Credenciales tipo "api": base_url + autenticación para el step api_call.
 *
 * A diferencia de DB Connections, editar usa PUT y NO borra/recrea: los
 * workflows guardan el credential_id, y recrearla les cambiaría el id.
 */
export function ApiConnections() {
  const { toast } = useToast()
  const { currentAccount } = useOrganization()
  const [connections, setConnections] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [deleteId, setDeleteId] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (currentAccount?.id) {
      localStorage.setItem("currentAccountId", currentAccount.id)
      reload()
    }
  }, [currentAccount?.id])

  // El listado solo trae metadatos; base_url y auth viven en el secreto (GET por id).
  // En el estado de la tabla solo se guardan campos no secretos.
  const reload = async () => {
    setLoading(true)
    try {
      const result = await credentialService.listApis()
      if (!result.success) throw new Error(result.error?.message || "Failed to load API connections")
      const rows = await Promise.all(
        result.data.map(async (cred) => {
          const detail = await credentialService.getCredential("api", cred.id)
          return detail.success
            ? toListRow(detail.data)
            : { id: cred.id, name: cred.name, detailError: true }
        })
      )
      setConnections(rows)
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" })
    } finally {
      setLoading(false)
    }
  }

  // Editar parte siempre de la credencial completa recién leída: si no se puede
  // leer, no se abre el diálogo (guardar desde un estado vacío borraría la auth).
  const openEdit = async (row) => {
    setBusyId(row.id)
    const detail = await credentialService.getCredential("api", row.id)
    setBusyId(null)
    if (!detail.success) {
      toast({ title: "Error", description: "Could not load this connection to edit it", variant: "destructive" })
      return
    }
    setEditing(detail.data)
    setDialogOpen(true)
  }

  const handleSave = async (form) => {
    setSaving(true)
    try {
      const payload = buildApiCredentialPayload(form, editing)
      const result = editing
        ? await credentialService.updateCredential("api", editing.id, payload)
        : await credentialService.createCredential(payload)
      if (!result.success) throw new Error(result.error?.message || "Failed to save connection")

      toast({ title: editing ? "API connection updated" : "API connection added" })
      setDialogOpen(false)
      setEditing(null)
      await reload()
    } catch (e) {
      toast({ title: "Error al guardar", description: e.message, variant: "destructive" })
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteId) return
    try {
      setBusyId(deleteId)
      const result = await credentialService.deleteCredential("api", deleteId)
      if (!result.success) throw new Error(result.error?.message || "Failed to delete connection")
      toast({ title: "API connection deleted" })
      await reload()
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" })
    } finally {
      setBusyId(null); setDeleteId(null)
    }
  }

  const openNew = () => { setEditing(null); setDialogOpen(true) }

  return (
    <div className="space-y-6 p-4 min-h-screen sm:space-y-8 sm:p-8">
      <div className="space-y-6 w-full">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-white">API Connections</h2>
              {connections.length > 0 && (
                <span className="rounded-full border border-white/10 px-2 py-0.5 text-xs text-gray-400">
                  {connections.length}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-400">
              External APIs your workflows call from API Call steps
            </p>
          </div>
          <Button onClick={openNew} className={`flex w-full items-center justify-center sm:w-auto ${PRIMARY}`}>
            <Plus className="w-4 h-4 mr-2" />
            Add Connection
          </Button>
        </div>

        {!loading && connections.length === 0 ? (
          <EmptyState
            icon={<Globe size={28} aria-hidden="true" />}
            title="No API connections yet"
            description="Save an API's base URL and credentials once, then pick it in any API Call step and only set the path."
            action={{ label: "Add connection", onClick: openNew }}
          />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0F1117]">
            <Table className="min-w-full divide-y divide-white/10">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-slate-300 font-semibold py-4">Name</TableHead>
                  <TableHead className="text-slate-300 font-semibold">Base URL</TableHead>
                  <TableHead className="text-slate-300 font-semibold">Auth</TableHead>
                  <TableHead className="text-slate-300 font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-16">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-b-4 border-purple-500 mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : (
                  connections.map((c) => (
                    <TableRow key={c.id} className="hover:bg-white/5 transition">
                      <TableCell className="font-medium text-white py-4">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-[#7c3aed]/20">
                            <Globe className="w-5 h-5 text-[#7c3aed]" />
                          </span>
                          <div>
                            <div className="font-bold">{c.name}</div>
                            <div className="text-[11px] font-mono text-slate-500">{c.id}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-slate-300 break-all">{c.detailError ? "Could not load details" : c.base_url || "-"}</TableCell>
                      <TableCell className="text-slate-400">{c.detailError ? "-" : authLabel(c.auth_type)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Actions" className="hover:bg-gray-800/80 rounded-full">
                              <span className="flex items-center gap-1">
                                <span className="w-1 h-1 bg-current rounded-full" />
                                <span className="w-1 h-1 bg-current rounded-full" />
                                <span className="w-1 h-1 bg-current rounded-full" />
                              </span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-black/90 border border-gray-800 rounded-xl">
                            <DropdownMenuItem
                              disabled={c.detailError || busyId === c.id}
                              className="hover:bg-gray-800 text-white"
                              onClick={() => openEdit(c)}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={busyId === c.id}
                              className="text-red-400 hover:bg-red-900/60 hover:text-red-300 rounded-lg"
                              onSelect={(e) => e.preventDefault()}
                              onClick={() => setDeleteId(c.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        <ApiConnectionDialog
          open={dialogOpen}
          onOpenChange={(o) => { if (!o) setEditing(null); setDialogOpen(o) }}
          connection={editing}
          onSave={handleSave}
          saving={saving}
        />

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent className="bg-black/80 backdrop-blur-2xl border border-purple-500/20 rounded-3xl max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-purple-300">Delete API Connection</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                This action is <span className="text-red-400 font-semibold">irreversible</span>. API Call steps that use this connection will fail until you pick another one. Are you sure?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6 flex justify-end space-x-4">
              <AlertDialogCancel asChild>
                <Button variant="secondary">Cancel</Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button className={`${BUTTON_SIZE} bg-red-600 text-white hover:bg-red-700`} onClick={confirmDelete}>
                  Delete
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
